/**
 * Parse a UTC-anchored date string (e.g. "2026-05-14T14:00:00.000Z") and
 * return a Date whose local date components match the UTC date (May 14),
 * regardless of the viewer's timezone.
 *
 * Background: the backend stores date-only values (lesson dates, booking dates)
 * as UTC midnight of the school's local day, e.g. AEST midnight = 14:00 UTC.
 * Constructing new Date(str) and formatting in local time shifts the day for
 * users in UTC+offset timezones. Using UTC components avoids this entirely.
 */
export function parseUTCDate(dateStr: string): Date {
  const d = new Date(dateStr);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Today at UTC midnight — the comparison anchor for `@db.Date` values.
 *
 * Those columns come back from Prisma at UTC midnight, so a date-only
 * comparison must be made against a UTC-midnight "today". Building it from
 * LOCAL components shifts the boundary by a day west of UTC, which is exactly
 * how two shipped date bugs (teacher dashboard, Teacher Tasks) were caused.
 *
 * Mirrors the backend helper of the same name.
 */
export function startOfTodayUTC(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/**
 * The SCHOOL's current calendar day, as a UTC-midnight `Date`.
 *
 * ⚠️ **This is NOT `startOfTodayUTC()`, and the difference is a shipped bug.**
 * Between 00:00 and 10:00 Sydney (11:00 under DST) UTC is still on YESTERDAY's
 * date, so `startOfTodayUTC()` judges yesterday to be "today" for ten hours of
 * every morning — inside the school's own business hours. The backend hit this
 * on the retro-edit audit gate (`hybridBooking.service.ts`, fixed 2026-09-18:
 * an absent audit row could not be backfilled, so each affected day was lost
 * permanently) and the frontend hit it again on the past-booking notice
 * (2026-09-19: the admin saw no warning while the server suppressed the family
 * email and wrote an audit row against their name).
 *
 * **Any "has this day already passed for the school?" question must use this.**
 * `startOfTodayUTC()` remains correct only for comparing two `@db.Date` values,
 * where both sides are tz-agnostic UTC midnights.
 *
 * Mirrors backend `utils/timezone.ts` `schoolTodayUtcMidnight` — including its
 * fail-closed fallback: an unusable zone yields the UTC day rather than
 * throwing, so a bad `School.timezone` degrades to the old behaviour instead of
 * breaking the page.
 */
export function schoolTodayUtcMidnight(timeZone: string, now: Date = new Date()): Date {
  const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(base);

    const get = (type: string): number =>
      Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const [year, month, day] = [get('year'), get('month'), get('day')];
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      throw new Error('unparseable parts');
    }
    return new Date(Date.UTC(year, month - 1, day));
  } catch {
    // Invalid IANA zone (Intl throws) or unparseable parts — fall back to the
    // UTC day, matching the backend's behaviour for an unusable zone.
    return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate()));
  }
}

/**
 * Format a Date as YYYY-MM-DD using LOCAL date components (not UTC).
 *
 * Used for HTML <input type="date"> values and any date-only string that
 * should reflect the user's calendar day. Avoids the off-by-one bug that
 * occurs with `new Date().toISOString().split('T')[0]` near midnight in
 * non-UTC timezones (e.g. AEST 09:00 = UTC 23:00 the previous day, so
 * toISOString returns yesterday's date).
 */
export function toLocalDateString(date: Date = new Date()): string {
  if (isNaN(date.getTime())) {
    throw new Error('toLocalDateString received an invalid Date');
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normalise a `@db.Date` value to a YYYY-MM-DD calendar-day key using UTC components.
 *
 * For values that are IDENTITY KEYS rather than display values — above all
 * `LessonException.originalDate`, which is half of the `@@unique([lessonId, originalDate])`
 * constraint. Such a key must round-trip to the backend byte-identical to what was stored,
 * so it must never be re-derived from a rendered local Date.
 *
 * A value already in `YYYY-MM-DD` form is returned unchanged (the backend's calendar
 * endpoints emit exactly this via `formatDateKey`).
 *
 * ⚠️ **Accepts only two shapes: a bare `YYYY-MM-DD` key, or a UTC-anchored instant carrying
 * an explicit `Z`/offset.** An offset-less datetime (`"2026-08-22T00:00:00"`) or a
 * non-zero-padded date (`"2026-8-22"`) is parsed as LOCAL by JS and would then be read with
 * UTC getters — silently reintroducing the very one-day shift this helper exists to prevent.
 * Those throw rather than returning a plausible wrong day. Do not "relax" this to be lenient:
 * a wrong date that looks right is the failure mode being defended against.
 *
 * Companion to `toLocalDateString`, which is the LOCAL-component equivalent used for values
 * that originate as a local wall-clock Date (e.g. a calendar drop target). Picking the wrong
 * one of the two shifts the day by one — pick by where the value CAME FROM, not by taste.
 */
export function toUTCDateKey(value: string | Date): string {
  if (typeof value === 'string') {
    // Already a bare calendar-day key — pass through untouched.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // Only an explicitly UTC-anchored instant may be reinterpreted. Anything else would be
    // parsed in local time and read back in UTC, which is a silent off-by-one day.
    if (!/^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
      throw new Error(
        `toUTCDateKey received an ambiguous date string: ${value}. ` +
          'Expected YYYY-MM-DD, or an ISO instant with an explicit Z/offset.'
      );
    }
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      throw new Error(`toUTCDateKey received an invalid date string: ${value}`);
    }
    return toUTCDateKey(parsed);
  }
  if (isNaN(value.getTime())) {
    throw new Error('toUTCDateKey received an invalid Date');
  }
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, '0');
  const day = String(value.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Build a Date at LOCAL midnight from a stored calendar day.
 *
 * The READ companion to `toLocalDateString`: whenever a `@db.Date` value will be handed to a
 * date picker or to date-fns `format()`, it must be rebuilt at LOCAL midnight, or the day you
 * READ differs from the day you WRITE. `parseUTCDate` is the wrong tool there — it returns UTC
 * midnight, which local getters then render as the previous day west of UTC.
 *
 * Accepts either a bare `YYYY-MM-DD` key or a UTC-anchored instant (the calendar day is taken
 * from its UTC components, matching how the backend stored it).
 *
 * This shipped as a bug three times (teacher dashboard → Teacher Tasks → Save the Date) while
 * the helper existed only as a page-local copy in `NotesAttendancePage`. It lives here now so
 * there is one implementation to fix.
 */
export function localDateFromKey(value: string | Date): Date {
  const key = toUTCDateKey(value);
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Today + N days as a YYYY-MM-DD local-date string. */
export function todayPlusDaysString(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
}

/**
 * Format a real instant (a Date carrying a true point-in-time) in the SCHOOL's timezone via
 * Intl (no date-fns-tz dependency — mirrors the existing `toLocaleString({ timeZone })` pattern
 * used elsewhere in the app). Use this for event START/END times so every viewer sees the
 * school's local wall-clock, not their own browser's.
 *
 * NOTE: pass a genuine instant here (e.g. an ISO string with offset, or a Date built to represent
 * the true UTC moment of the event). For date-ONLY values (@db.Date columns stored at UTC
 * midnight — dueDate, originalDate, term dates) use `formatUTCDate` instead: their day is
 * tz-agnostic and must be read in UTC, not shifted into the school tz.
 */
export function formatInSchoolTz(
  instant: Date | string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-AU'
): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(d);
}

/** The school-zone wall clock of an instant, as `{ y, mo, d, h, mi }` numbers. */
function schoolWallClockParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(instant);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: n('year'), mo: n('month'), d: n('day'), h: n('hour'), mi: n('minute') };
}

/**
 * An instant as the SCHOOL's wall clock, `YYYY-MM-DDTHH:mm` (the value a `datetime-local` input
 * takes), so a time typed for the school is the school's, never the browser's.
 */
export function instantToSchoolWallClock(instant: Date, timeZone: string): string {
  const p = schoolWallClockParts(instant, timeZone);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/**
 * The instant at which the school's wall clock reads `YYYY-MM-DDTHH:mm`. Two passes of the zone's
 * offset, so a value either side of a DST change resolves; a time inside a spring-forward gap lands
 * an hour later. Null for a malformed value.
 */
export function schoolWallClockToInstant(value: string, timeZone: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const offsetAt = (t: number) => {
    const p = schoolWallClockParts(new Date(t), timeZone);
    return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi) - t;
  };
  let guess = asUtc - offsetAt(asUtc);
  guess = asUtc - offsetAt(guess);
  return new Date(guess);
}

/**
 * Format a date-ONLY value (a `@db.Date` column serialised as UTC midnight, e.g. an invoice
 * dueDate or a lesson exception originalDate) showing its stored calendar day. Reads in UTC so
 * the day never shifts by the viewer's — or the server's — offset. This is the display companion
 * to `parseUTCDate`.
 */
export function formatUTCDate(
  value: Date | string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
  locale = 'en-AU'
): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
}

/**
 * The Monday (UTC midnight) of the week containing the term's start date.
 *
 * Mirrors backend `utils/termWeeks.ts:termWeekAnchor`: term weeks ALWAYS run
 * Mon–Sun, so a term starting mid-week (e.g. the Tuesday after a public
 * holiday) gets a short week 1 instead of weeks that straddle Monday. For
 * Monday-starting terms this is the term start itself.
 */
export function termWeekAnchor(termStartDate: string | Date): Date {
  const start =
    typeof termStartDate === 'string' ? new Date(termStartDate) : new Date(termStartDate);
  const anchor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  anchor.setUTCDate(anchor.getUTCDate() - ((anchor.getUTCDay() + 6) % 7));
  return anchor;
}

/**
 * Calculate the term-relative week number for a given date.
 *
 * Mirrors backend `utils/termWeeks.ts:getWeekNumber` so hybrid pattern checks
 * (`individualWeeks.includes(currentWeekNumber)`) match server-side semantics.
 * Week 1 = the Mon–Sun week containing the term start (termWeekAnchor).
 * Returns 1-indexed week number. Values outside the term range can be < 1 or
 * > the term length; callers should bounds-check against the term's dates.
 */
export function currentWeekNumber(termStartDate: string | Date, targetDate: Date = new Date()): number {
  const start = termWeekAnchor(termStartDate);
  const target = new Date(targetDate);
  target.setUTCHours(0, 0, 0, 0);

  const diffMs = target.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.floor(diffDays / 7) + 1;
}

/**
 * Mirrors backend `utils/termWeeks.ts:MAX_TERM_WEEKS` — the transport ceiling
 * on a week number (picker length, validator cap). NOT a term bound; a term's
 * real length is `termWeekCount`.
 */
export const MAX_TERM_WEEKS = 15;

/**
 * How many Monday-anchored weeks a term has — the week number of its last day.
 * Mirrors backend `utils/termWeeks.ts:termWeekCount` (#169). Accepts the
 * `@db.Date` ISO strings terms arrive as. NEVER `round`/`ceil(days / 7)`:
 * that undercounts any term ending Mon–Thu. Floors at 1.
 */
export function termWeekCount(termStartDate: string | Date, termEndDate: string | Date): number {
  const end = typeof termEndDate === 'string' ? parseUTCDate(termEndDate) : termEndDate;
  return Math.max(1, currentWeekNumber(termStartDate, end));
}

/**
 * The occurrence date (UTC midnight) of `dayOfWeek` (0=Sun…6=Sat) in term
 * week `weekNumber`. Mirrors backend `utils/termWeeks.ts:getDateForWeek`.
 * With Monday anchoring, a week-1 day earlier than the term start falls
 * BEFORE the term — callers must bounds-check against the term's dates.
 */
export function occurrenceDateForWeek(
  termStartDate: string | Date,
  weekNumber: number,
  dayOfWeek: number
): Date {
  const anchor = termWeekAnchor(termStartDate);
  const target = new Date(anchor);
  target.setUTCDate(target.getUTCDate() + (weekNumber - 1) * 7 + ((dayOfWeek + 6) % 7));
  return target;
}


// ===========================================
// Class week window (mid-term start / end week, 2026-09-30)
// ===========================================
// A class runs in ONE window of its term: [startWeek ?? 1, endWeek ?? termWeeks],
// both inclusive. A student's effective start is max(class start, enrolment start).
// These five functions are THE definition — every "does this class run in week N"
// copy (occurrence resolver, calendars, conflict engine, rolls, billing, pickers)
// reads the window through them and never re-derives it inline. Mirrors backend
// `utils/termWeeks.ts`; one shared WINDOW_CASES fixture pins the pair.
// Plan: md/plan/mid-term-start-week.md.

/** The two nullable columns, as they sit on `Lesson` (or on a candidate not yet saved). */
export interface LessonWindowSource {
  startWeek?: number | null;
  endWeek?: number | null;
}

/** Inclusive week range. `first > last` means the class runs in NO week of the term. */
export interface LessonWeekWindow {
  first: number;
  last: number;
}

/**
 * The weeks a class runs in, clipped to its term. `termWeeks` is `termWeekCount`.
 * A stored week past the term end (a term shortened after the fact) yields an EMPTY
 * window rather than throwing — every caller must stay safe on stale data.
 */
export function lessonWeekWindow(lesson: LessonWindowSource, termWeeks: number): LessonWeekWindow {
  return {
    first: Math.max(1, lesson.startWeek ?? 1),
    last: Math.min(termWeeks, lesson.endWeek ?? termWeeks),
  };
}

/** Does the class run in term week `week`? False for a non-integer or out-of-term week. */
export function weekInLessonWindow(week: number, lesson: LessonWindowSource, termWeeks: number): boolean {
  if (!Number.isInteger(week)) return false;
  const { first, last } = lessonWeekWindow(lesson, termWeeks);
  return first <= week && week <= last;
}

/**
 * The first week THIS student attends: the later of the class's start and their own.
 * An enrolment start earlier than the class start cannot pull the class forward.
 */
export function effectiveStartWeek(
  lesson: LessonWindowSource,
  enrollment?: { startWeek?: number | null } | null
): number {
  return Math.max(lesson.startWeek ?? 1, enrollment?.startWeek ?? 1);
}

/** Does the class run in `week` AND has this student started by then? */
export function weekInEnrollmentWindow(
  week: number,
  lesson: LessonWindowSource,
  enrollment: { startWeek?: number | null } | null | undefined,
  termWeeks: number
): boolean {
  return week >= effectiveStartWeek(lesson, enrollment) && weekInLessonWindow(week, lesson, termWeeks);
}

/** Every week the class runs in, ascending; `[]` for an empty window. Billing and bands. */
export function lessonWindowWeeks(lesson: LessonWindowSource, termWeeks: number): number[] {
  const { first, last } = lessonWeekWindow(lesson, termWeeks);
  const weeks: number[] = [];
  for (let week = first; week <= last; week++) weeks.push(week);
  return weeks;
}

/**
 * `Week 5 09/11/2026` for a term week (CLAUDE.md Critical Rule 7): the class's own day in
 * that week, or the week's Monday when the class has no fixed day (`dayOfWeek` null).
 * A `@db.Date` day, so it is formatted with UTC getters via `formatUTCDate`.
 */
export function formatTermWeekDate(
  termStartDate: string | Date,
  week: number,
  dayOfWeek: number | null | undefined
): string {
  const day = occurrenceDateForWeek(termStartDate, week, dayOfWeek ?? 1);
  return `Week ${week} ${formatUTCDate(day, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU')}`;
}
